import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredMovies = movies.filter((m: Movie) => m.categorySlug === slug);

  const displayMovies = filteredMovies.length > 0 ? filteredMovies : movies;

  const handleCategoryChange = (newSlug: string) => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    navigate(`/category/${newSlug}`);
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yayacinema.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: 'https://www.yayacinema.com/category/' + slug },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <nav className="w-full px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 max-w-7xl mx-auto">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
                首页
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <span className="text-foreground-900 font-medium">{currentCategory.name}</span>
            </li>
          </ol>
        </nav>

        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {displayMovies.length} 部作品
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {displayMovies.map((movie: Movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-xl mb-3 aspect-[3/4]">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      title={`${movie.title} - 丫丫影院免费在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                        {movie.rating}
                      </span>
                    </div>
                    {movie.isNew && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                          NEW
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-500">
                      {movie.year}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {movie.region}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </>
  );
}